import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, Check } from 'lucide-react';
import { useLevels } from '../../hooks/useLevels';
import { iconFor, getLevelForScore } from '../../utils/levels';
import './RanksDialog.css';

const formatRange = (lvl) => {
  if (lvl.max_points == null) return `${lvl.min_points.toLocaleString()}+ pts`;
  return `${lvl.min_points.toLocaleString()} – ${lvl.max_points.toLocaleString()} pts`;
};

const RanksDialog = ({ open, onClose, currentScore = 0 }) => {
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const { levels, loading } = useLevels();
  const current = getLevelForScore(levels, currentScore);
  const currentIdx = current ? levels.findIndex((l) => l.id === current.id) : -1;

  useEffect(() => {
    if (!open) return undefined;

    triggerRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const t = window.setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(t);
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="rd-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="rd-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rd-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="rd-head">
          <div>
            <h2 id="rd-title" className="rd-title">Rank Tiers</h2>
            <p className="rd-sub">Earn points each round to climb the ladder.</p>
          </div>
          <button
            type="button"
            className="rd-close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </header>

        {loading && levels.length === 0 ? (
          <ul className="rd-list">
            {[1, 2, 3, 4, 5].map((i) => (
              <li key={i} className="rd-row rd-row-skeleton" aria-hidden="true" />
            ))}
          </ul>
        ) : levels.length === 0 ? (
          <div className="rd-empty">No levels configured yet.</div>
        ) : (
          <ul className="rd-list">
            {levels.map((lvl, i) => {
              const Icon       = iconFor(lvl.icon);
              const isCurrent  = i === currentIdx;
              const isAchieved = currentIdx >= 0 && i < currentIdx;
              const isLocked   = currentIdx >= 0 && i > currentIdx;

              let statusNode = null;
              if (isCurrent) {
                statusNode = <span className="rd-status rd-status-current" style={{ color: lvl.color, borderColor: `${lvl.color}55`, background: `${lvl.color}1a` }}>Current</span>;
              } else if (isAchieved) {
                statusNode = <span className="rd-status rd-status-done"><Check size={11} strokeWidth={3} /> Achieved</span>;
              } else if (isLocked) {
                statusNode = <span className="rd-status rd-status-locked"><Lock size={11} /> Locked</span>;
              }

              return (
                <li
                  key={lvl.id}
                  className={`rd-row${isCurrent ? ' is-current' : ''}${isLocked ? ' is-locked' : ''}`}
                  style={isCurrent ? { borderColor: `${lvl.color}66`, background: `linear-gradient(90deg, ${lvl.color}12, transparent 70%)` } : undefined}
                >
                  <div
                    className="rd-icon"
                    style={{ color: lvl.color, background: `${lvl.color}1a`, borderColor: `${lvl.color}40` }}
                  >
                    <Icon size={18} strokeWidth={2.2} />
                  </div>
                  <div className="rd-info">
                    <div className="rd-name-row">
                      <span className="rd-name" style={{ color: isLocked ? undefined : lvl.color }}>{lvl.name}</span>
                      {statusNode}
                    </div>
                    <span className="rd-range">{formatRange(lvl)}</span>
                    {lvl.blurb && <span className="rd-blurb">{lvl.blurb}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default RanksDialog;
